// externals
// eslint-disable-next-line @typescript-eslint/no-var-requires
const uniqueId = require('lodash.uniqueid');

// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { FeatureToggles } from '@makemydeal/dr-platform-shared';
import type {
    CreditScoreSource,
    CreditTiers,
    DealerSource,
    GenericObject,
    IncompleteDealDetails,
    IPenciledInputs,
    IPencilOfferInputs,
    IShopper,
    OfferIncentives,
    OfferSettings,
    OfferType,
    PenciledInputOfferType,
    Terms,
    Vehicle
} from '@makemydeal/dr-platform-types';
import { CASH, CashOptions, FINANCE, LEASE, BALLOON, OFFER_ENCRYPTION_FIELDS } from '@makemydeal/dr-platform-types';

// utils
import { getInitialFeesOverride } from '../selectors/utils/nonSelectorUtils';

// interfaces/types
import { INIT_PENCIL_INVALID_DEAL, INIT_PENCIL_SUCCESS } from '../types/actionTypes';
import type { ExtendedFeatureToggles, InitOfferPayload, PartialInitOfferPayload } from '../types/commonTypes';
import {
    buildBalloonOfferNode,
    buildCashOfferNode,
    buildFinanceOfferNode,
    buildLeaseOfferNode,
    dropCustomIncentiveIds,
    getSavedSellRateOverrideUnit
} from './offerNodeUtils';
import type { InitOfferCreditTiersActionPayload } from '../actions/payloadTypes';

// constants
import Config from '../config';
import { getDefaultTerms, hasUserTermOverrides, mapLeaseQuoteType } from '../utils';
import { syncDefaultTermIds } from '../utils/termUtils';
import { getLoadPayloadFeatureToggles } from '../utils/loadPayloadFeatureToggles';
import { CALC_ENDPOINTS } from '../constants/state';
import { ASP_PG_REDESIGN_TOGGLE } from '../constants/meta';
import type { OfferTermOffer } from '../types/offerTypes';

// Needed to store mappings to ids as currently passing context isnt
// available between maping functions.
const mappings: {
    tiers?: GenericObject;
    tierWithNumberIds: GenericObject[];
} = {
    tiers: undefined,
    tierWithNumberIds: []
};

function selectSettingsFromPayload(payload: any): OfferSettings {
    // TODO: Found Dealer type in dr-shared-types but it has few params missing those are used here.
    const { dealer } = payload;
    return {
        isLeaseEnabled: dealer?.products?.hasLeasing,
        isCashEnabled: dealer?.cashOfferSettings?.hasCashOffer || false,
        baseLeaseOnMSRP: dealer?.leaseSettings?.isLeasePaymentOnMSRP,
        isNewSinglePrice: dealer.isNewVehicleSinglePrice,
        isUsedSinglePrice: dealer.isUsedVehicleSinglePrice,
        defaultOfferType: dealer.defaultOfferType,
        dealerId: dealer.id,
        ddcAccountId: dealer.ddcAccountId,
        isDueAtSigninAmountDeduct: dealer?.leaseSettings?.isDueAtSigninAmountDeduct
    };
}

export function selectCreditTiersFromPayload(payload: InitOfferCreditTiersActionPayload): CreditTiers {
    const { dealer } = payload;
    const creditTiers = dealer?.creditTiers;
    const tiers: {
        byId: GenericObject;
        allIds: string[];
        byFinance: string[];
        byLease: string[];
        byBalloon: string[];
    } = {
        byId: {
            /*index by id*/
        },
        allIds: [
            /*array of ids*/
        ],
        byFinance: [
            /*array of ids*/
        ],
        byLease: [
            /*array of ids*/
        ],
        byBalloon: [
            /*array of ids*/
        ]
    };

    if (!creditTiers) {
        return {} as CreditTiers;
    }

    // HACK So we can remember old identifiers
    mappings.tiers = {};

    const toTierType = (type: OfferType) => {
        return (t: any) => {
            const lid = t.id;
            const cachedTier = mappings.tierWithNumberIds.find((tier) => tier.highScoreId === lid);
            let tierId = '';

            if (cachedTier) {
                tierId = cachedTier.id;
            } else {
                tierId = uniqueId('tier_');
                // Saving the Number so I can use it to compare to the selectedCreditTierId
                mappings.tierWithNumberIds.push({ id: tierId, highScoreId: lid });
            }

            // So we can look up the selected tier in later mapping.
            mappings.tiers![`${type}_${lid}`] = tierId;

            t.id = tierId;
            tiers.byId[tierId] = t;
            tiers.allIds.push(tierId);
            if (type === LEASE) tiers.byLease.push(tierId);
            if (type === FINANCE) tiers.byFinance.push(tierId);
            if (type === BALLOON) tiers.byBalloon.push(tierId);
        };
    };

    // Process finance
    creditTiers[FINANCE]?.forEach(toTierType(FINANCE));
    const hasFinanceTiers = tiers.byFinance.length > 0;

    // Process Lease
    if (creditTiers[LEASE]) {
        if (hasFinanceTiers) {
            tiers.byLease = [...tiers.byFinance];
            // make sure lease tier IDs match finance tier IDs
            creditTiers[LEASE].forEach((tier) => {
                const financeTierId = mappings.tiers?.[`finance_${tier.id}`];
                if (financeTierId) {
                    tier.id = financeTierId;
                } else {
                    // eslint-disable-next-line no-console
                    console.warn(`unable to find matching finance tier for lease tier ID: ${tier.id}`);
                }
            });
        } else {
            creditTiers[LEASE].forEach(toTierType(LEASE));
        }
    }

    // Process Balloon
    if (creditTiers[BALLOON]) {
        creditTiers[BALLOON].forEach(toTierType(BALLOON));
    }
    // Process cash tiers to cover line  else branch (tiers.byLease.push)

    return tiers;
}

export function getSelectedCreditTier(offer: any, tiers: any, tierWithNumberIds: any): string {
    let selectedCreditTierId;
    // Check for tiers here
    if (tiers && tierWithNumberIds && tierWithNumberIds.length) {
        // looking at the mappings of the tiers to see if the selected Credit Tier is there.
        selectedCreditTierId = tiers[`finance_${offer?.finance?.selectedCreditTierId}`];
        // If it doesn't find the credit tier.
        if (!selectedCreditTierId && offer[FINANCE]?.selectedCreditTierId) {
            // Loop through the sorted array highest to lowest
            // and get the id of the one that is the closet to the original id
            // but still more than it.
            tierWithNumberIds.forEach((tier: any) => {
                if (tier.highScoreId > offer[FINANCE].selectedCreditTierId) {
                    selectedCreditTierId = tier.id;
                }
            });
        }
    }
    return selectedCreditTierId;
}

const selectLeaseAnnualMilesFromPayload = (payload: GenericObject): number[] | undefined => {
    return payload?.dealer?.leaseSettings?.annualMilesOptions;
};

export const selectTermsFromPayload = (payload: GenericObject): Terms => {
    const payloadTerms = payload?.offer?.terms;

    if (!payloadTerms) {
        return getDefaultTerms();
    }

    const terms = getDefaultTerms();
    return { ...terms, ...payloadTerms };
};

function selectIncentivesFromPayload(payload: GenericObject): OfferIncentives | undefined {
    return payload?.offer?.incentives;
}

function selectFeatureTogglesFromPayload(payload: any): ExtendedFeatureToggles | undefined {
    return getLoadPayloadFeatureToggles(payload) as ExtendedFeatureToggles | undefined;
}

function selectTogglesFromPayload(payload: any): FeatureToggles | undefined {
    return payload?.toggles;
}

export const selectPenciledInputsFromPayload = (payload: GenericObject): IPenciledInputs => {
    const { offer, dealer, template }: { offer: GenericObject; dealer: any; vehicle: any; template: any } = payload as any;
    const inputs: IPenciledInputs = {
        byOfferType: {} as { [offerType in PenciledInputOfferType]: IPencilOfferInputs },
        allTypes: [
            /*array of ids*/
        ]
    };

    inputs.allTypes = [CASH, FINANCE, LEASE, BALLOON];
    const hasLeasing = dealer?.leaseSettings?.hasLeasing;

    const initialCalculationEndpoint =
        payload.calculationDetails?.payment?.calculationEngine?.endpoint ?? CALC_ENDPOINTS.programQuotes;
    const isUserProgramQuotes = initialCalculationEndpoint === CALC_ENDPOINTS.userProgramQuotes;

    // Add finance inputs defaults

    const selectedCreditTierId = getSelectedCreditTier(offer, mappings.tiers, mappings.tierWithNumberIds);
    const financeCreditTier = selectedCreditTierId || offer?.finance?.selectedCreditTierId;
    const leaseCreditTier =
        (mappings.tiers
            ? mappings.tiers[`${FINANCE}_${offer?.lease?.selectedCreditTierId}`]
            : offer?.lease?.selectedCreditTierId) || selectedCreditTierId;
    const balloonCreditTier =
        (mappings.tiers
            ? mappings.tiers[`${FINANCE}_${offer?.balloon?.selectedCreditTierId}`]
            : offer?.balloon?.selectedCreditTierId) || selectedCreditTierId;

    // TODO: here code defaults values to null and therefore, eventhough lease is not enabled, it won't result in error
    // however, app should try to avoid lease calculations as much as possible if lease is not enabled because there may be
    // some downstream effects.

    const leaseOffer = offer[LEASE] ? { ...offer[LEASE] } : undefined;
    if (leaseOffer) {
        leaseOffer.selectedAnnualMiles ||= payload?.dealer?.leaseSettings?.defaultAnnualMiles || 0;
    }

    const isTemplateApplied = template?.templateApplied;
    const withPaymentTerms = template?.withPaymentTerms;
    const loadedFeatureToggles = getLoadPayloadFeatureToggles(payload);
    const enableCustomRateCard = loadedFeatureToggles?.enableCustomRateCard;
    const enableOverrideTaxFixes = loadedFeatureToggles?.overrideTaxFixes_US1984109;
    const isEquityAllocationOrderEnabled = loadedFeatureToggles?.enableEquityAllocationOrder;
    // When rate card is enabled, only use UPQ for templates that carry explicit payment terms.
    // Templates without rates should fall through to PQ so the rate card is applied (AC 6 & AC 9).
    const financeUserProgramQuotes = isUserProgramQuotes || (isTemplateApplied && (!enableCustomRateCard || withPaymentTerms));

    // Use a shallow copy of the offer with the cloned lease data to avoid mutating the original payload
    const offerWithDefaults = leaseOffer ? { ...offer, [LEASE]: leaseOffer } : offer;

    inputs.byOfferType![FINANCE] = buildFinanceOfferNode(
        offerWithDefaults as OfferTermOffer,
        financeCreditTier,
        financeUserProgramQuotes,
        enableOverrideTaxFixes
    );
    inputs.byOfferType![LEASE] = buildLeaseOfferNode(
        offerWithDefaults as OfferTermOffer,
        leaseCreditTier,
        isUserProgramQuotes,
        enableOverrideTaxFixes,
        isEquityAllocationOrderEnabled
    );
    inputs.byOfferType![BALLOON] = buildBalloonOfferNode(
        offerWithDefaults as OfferTermOffer,
        balloonCreditTier,
        isUserProgramQuotes,
        enableOverrideTaxFixes
    );

    // default state of 9box in SV
    const { showPaymentRange, financeRangeAmount, leaseRangeAmount, showFlexiblePaymentOptionsSalesView } = dealer;
    inputs.byOfferType![FINANCE].paymentGridToggleSP = showPaymentRange ? !financeRangeAmount : showFlexiblePaymentOptionsSalesView;
    if (hasLeasing) {
        inputs.byOfferType![LEASE].paymentGridToggleSP = showPaymentRange ? !leaseRangeAmount : showFlexiblePaymentOptionsSalesView;

        // Apply dealer's default lease rate type only if no rate type is already set
        const defaultLeaseQuoteType = payload?.dealer?.leaseSettings?.defaultLeaseQuoteType;
        const isRedesignEnabled = Boolean(
            loadedFeatureToggles?.[ASP_PG_REDESIGN_TOGGLE] ?? payload.scenarioFeatureToggles?.[ASP_PG_REDESIGN_TOGGLE]
        );
        if (isRedesignEnabled) {
            if (defaultLeaseQuoteType && !inputs.byOfferType![LEASE].rateTypeOverride) {
                const savedLeaseSellRateUnit = getSavedSellRateOverrideUnit(offerWithDefaults as OfferTermOffer, LEASE);
                inputs.byOfferType![LEASE].rateTypeOverride = savedLeaseSellRateUnit ?? mapLeaseQuoteType(defaultLeaseQuoteType);
            }
        } else if (defaultLeaseQuoteType && !inputs.byOfferType![LEASE].rateTypeOverride) {
            inputs.byOfferType![LEASE].rateTypeOverride = mapLeaseQuoteType(defaultLeaseQuoteType);
        }
    }

    const cashOffer = offer?.[CASH];
    if (cashOffer) {
        inputs.byOfferType![CASH] ||= { ...offer.payment, ...buildCashOfferNode(cashOffer) };
        if (loadedFeatureToggles?.DE783842_Enable_Cash_Offer_Price_Fallback && !inputs.byOfferType![CASH].offerPrice) {
            inputs.byOfferType![CASH].offerPrice = offer.offerPrice || inputs.byOfferType![FINANCE]?.offerPrice;
        }
    }

    if (loadedFeatureToggles?.enableProgramIncentivesRestoration_F292564 && inputs.byOfferType) {
        (Object.values(inputs.byOfferType) as IPencilOfferInputs[]).forEach((node) => {
            node.selectedIncentives = dropCustomIncentiveIds(
                node.selectedIncentives,
                (offer as any).incentives,
                (offer as any).payment?.incentives
            );
        });
    }

    if (offer.dealXgDetails?.honorOverrides && inputs.byOfferType) {
        const enableManualIncentivesDxgPersistence = loadedFeatureToggles?.enableManualIncentivesDxgPersistence;

        if (enableManualIncentivesDxgPersistence) {
            // When toggle is ON, apply honor overrides to all offer types unconditionally
            Object.keys(inputs.byOfferType).forEach((type) => {
                if (inputs.byOfferType?.[type as keyof typeof inputs.byOfferType]) {
                    inputs.byOfferType![type as keyof typeof inputs.byOfferType].feesOverride = getInitialFeesOverride(
                        offer as any
                    );
                    inputs.byOfferType![type as keyof typeof inputs.byOfferType][OFFER_ENCRYPTION_FIELDS.INCENTIVES] =
                        selectExtraData2FromPayload(payload);
                    (inputs.byOfferType as any)[type][OFFER_ENCRYPTION_FIELDS.OVERRIDES] = selectExtraData3FromPayload(payload);
                    inputs.byOfferType![type as keyof typeof inputs.byOfferType].honorOverrides = true;
                }
            });

            // For balloon deals, also copy balloon-specific data to other offer types for tab switching
            const offerType = offer.type as keyof typeof inputs.byOfferType;
            if (offerType === BALLOON && !isTemplateApplied) {
                const balloonInputs = inputs.byOfferType[BALLOON];
                // US2075533: guarded on real overrides, not presence. Every priced term now carries a rate
                // type, so a balloon deal's map is always populated — a presence check would fire on every
                // reload and replace Finance's own saved per-term overrides with balloon's labels.
                if (hasUserTermOverrides(balloonInputs?.overridesByTerm)) {
                    inputs.byOfferType[FINANCE].overridesByTerm = balloonInputs!.overridesByTerm;
                }
                if (balloonInputs?.sellRateOverride != null) {
                    inputs.byOfferType[FINANCE].sellRateOverride = balloonInputs.sellRateOverride;
                }
                if (balloonInputs?.buyRateOverride != null) {
                    inputs.byOfferType[FINANCE].buyRateOverride = balloonInputs.buyRateOverride;
                }
                if (balloonInputs?.rateTypeOverride != null) {
                    inputs.byOfferType[FINANCE].rateTypeOverride = balloonInputs.rateTypeOverride;
                }

                if (balloonInputs?.registrationType) {
                    inputs.byOfferType[FINANCE].registrationType = balloonInputs.registrationType;
                    inputs.byOfferType[CASH].registrationType = balloonInputs.registrationType;
                    inputs.byOfferType[LEASE].registrationType = balloonInputs.registrationType;
                }

                if (balloonInputs?.offerPrice != null) {
                    inputs.byOfferType[FINANCE].offerPrice = balloonInputs.offerPrice;
                    inputs.byOfferType[CASH].offerPrice = balloonInputs.offerPrice;
                    inputs.byOfferType[LEASE].offerPrice = balloonInputs.offerPrice;
                }
                if (balloonInputs?.downPayment != null) {
                    inputs.byOfferType[FINANCE].downPayment = balloonInputs.downPayment;
                    inputs.byOfferType[CASH].downPayment = balloonInputs.downPayment;
                    inputs.byOfferType[LEASE].downPayment = balloonInputs.downPayment;
                }
            }
        } else {
            // Original behavior: apply to current offer type, and to others only when template is applied
            const offerType = offer.type as keyof typeof inputs.byOfferType;
            inputs.byOfferType[offerType].feesOverride = getInitialFeesOverride(offer as any);
            inputs.byOfferType[offerType][OFFER_ENCRYPTION_FIELDS.INCENTIVES] = selectExtraData2FromPayload(payload);
            (inputs.byOfferType[offerType] as any)[OFFER_ENCRYPTION_FIELDS.OVERRIDES] = selectExtraData3FromPayload(payload);
            inputs.byOfferType[offerType].honorOverrides = true;

            // For balloon deals in SV, copy balloon data to other offer types for tab switching
            if (offerType === BALLOON && !isTemplateApplied) {
                inputs.byOfferType[FINANCE].feesOverride = getInitialFeesOverride(offer as any);
                inputs.byOfferType[FINANCE][OFFER_ENCRYPTION_FIELDS.INCENTIVES] = selectExtraData2FromPayload(payload);
                (inputs.byOfferType as any)[FINANCE][OFFER_ENCRYPTION_FIELDS.OVERRIDES] = selectExtraData3FromPayload(payload);
                inputs.byOfferType[FINANCE].honorOverrides = true;

                const balloonInputs = inputs.byOfferType[BALLOON];
                // US2075533: guarded on real overrides, not presence. Every priced term now carries a rate
                // type, so a balloon deal's map is always populated — a presence check would fire on every
                // reload and replace Finance's own saved per-term overrides with balloon's labels.
                if (hasUserTermOverrides(balloonInputs?.overridesByTerm)) {
                    inputs.byOfferType[FINANCE].overridesByTerm = balloonInputs!.overridesByTerm;
                }
                if (balloonInputs?.sellRateOverride != null) {
                    inputs.byOfferType[FINANCE].sellRateOverride = balloonInputs.sellRateOverride;
                }
                if (balloonInputs?.buyRateOverride != null) {
                    inputs.byOfferType[FINANCE].buyRateOverride = balloonInputs.buyRateOverride;
                }
                if (balloonInputs?.rateTypeOverride != null) {
                    inputs.byOfferType[FINANCE].rateTypeOverride = balloonInputs.rateTypeOverride;
                }

                if (balloonInputs?.registrationType) {
                    inputs.byOfferType[FINANCE].registrationType = balloonInputs.registrationType;
                    inputs.byOfferType[CASH].registrationType = balloonInputs.registrationType;
                    inputs.byOfferType[LEASE].registrationType = balloonInputs.registrationType;
                }

                if (balloonInputs?.offerPrice != null) {
                    inputs.byOfferType[FINANCE].offerPrice = balloonInputs.offerPrice;
                    inputs.byOfferType[CASH].offerPrice = balloonInputs.offerPrice;
                    inputs.byOfferType[LEASE].offerPrice = balloonInputs.offerPrice;
                }
                if (balloonInputs?.downPayment != null) {
                    inputs.byOfferType[FINANCE].downPayment = balloonInputs.downPayment;
                    inputs.byOfferType[CASH].downPayment = balloonInputs.downPayment;
                    inputs.byOfferType[LEASE].downPayment = balloonInputs.downPayment;
                }
            }

            if (isTemplateApplied) {
                const offerTypes = [FINANCE, LEASE, CASH, BALLOON];
                offerTypes
                    .filter((type) => type !== offer.type)
                    .forEach((type: any) => {
                        (inputs.byOfferType as any)[type].feesOverride = getInitialFeesOverride(offer as any);
                        (inputs.byOfferType as any)[type][OFFER_ENCRYPTION_FIELDS.INCENTIVES] =
                            selectExtraData2FromPayload(payload);
                        (inputs.byOfferType as any)[type][OFFER_ENCRYPTION_FIELDS.OVERRIDES] = selectExtraData3FromPayload(payload);
                        (inputs.byOfferType as any)[type].honorOverrides = true;
                    });
            }
        }
    }

    // default state if overrides exist
    if (offer.dealXgDetails?.honorOverrides) {
        inputs.byOfferType![FINANCE].paymentGridToggleSP = false;
        inputs.byOfferType![LEASE].paymentGridToggleSP = false;
    }

    return inputs;
};

function selectShopperFromPayload(payload: any): IShopper {
    const { offer } = payload;
    // Do NOT set the shopper's zip code to be the same as the dealer's zip code initially
    const shopper = offer.shopper || {};
    return { ...{ zip: '' }, ...shopper }; // Defaulting to empty string here
}

function selectCoBuyerFromPayload(payload: any) {
    const { offer } = payload;
    // Do NOT set the shopper's zip code to be the same as the dealer's zip code initially
    return offer.coBuyer ?? null;
}

function selectMessageFromPayload(payload: any): string {
    const { offer } = payload;
    return offer.message || '';
}

function selectExtraDataFromPayload(payload: any): string[] | string {
    const { offer } = payload;
    return offer[OFFER_ENCRYPTION_FIELDS.PAYMENT] || '';
}

function selectExtraData2FromPayload(payload: any): string {
    const { offer } = payload;
    return offer[OFFER_ENCRYPTION_FIELDS.INCENTIVES] || '';
}

function selectExtraData3FromPayload(payload: any): string[] | string {
    const { offer } = payload;
    return offer[OFFER_ENCRYPTION_FIELDS.OVERRIDES] || '';
}

const Conditions: Record<string, string> = { new: 'New', used: 'Used', certified: 'Certified' };
export function selectVehicleFromPayload(payload: any): Vehicle {
    const { vehicle = {} } = payload;
    const vehicleCondition = (vehicle.type ?? vehicle.condition ?? Conditions.used).toLowerCase();
    const condition = Conditions[vehicleCondition] || Conditions.used;

    return {
        ...vehicle,
        condition,
        type: condition.toLowerCase()
    };
}

const DEFAULT_CREDIT_SCORE = { description: '', high: 0, low: 0 };

export function getOfferFromInitPayload(payload: any) {
    return (payload && payload.offer) || {};
}

function selectCreditScoreFromPayload(payload: any) {
    const offer = getOfferFromInitPayload(payload);
    return offer.creditScore || DEFAULT_CREDIT_SCORE;
}

function selectCreditScoreSourceFromPayload(payload: any): CreditScoreSource | undefined {
    const offer = getOfferFromInitPayload(payload);
    return offer.creditScoreSource;
}

function selectTotalFinancedFromPayload(payload: any): number {
    const offer = getOfferFromInitPayload(payload);
    return offer.totalFinanced || 0;
}

function selectMonthlyPaymentFromPayload(payload: any): number {
    const offer = getOfferFromInitPayload(payload);
    return offer.monthlyPayment || 0;
}

function selectMonthlyPaymentWithoutProductsFromPayload(payload: any): number {
    const offer = getOfferFromInitPayload(payload);
    return offer.monthlyPaymentWithoutProducts || 0;
}

function selectSessionIdFromPayload(payload: any): string {
    const offer = getOfferFromInitPayload(payload);
    return offer.sessionId || '';
}

function selectDealExchangeDealIdFromPayload(payload: any): string {
    const offer = getOfferFromInitPayload(payload);
    return offer.dealExchangeDealId || '';
}

function selectDealExchangeVersionIdFromPayload(payload: any): string {
    const offer = getOfferFromInitPayload(payload);
    return offer.dealExchangeVersionId || '';
}

function selectCashApplyMethodFromPayload(payload: any): string | null {
    const offer = getOfferFromInitPayload(payload);
    return offer.type === LEASE ? offer.cashApplyMethod : null;
}

function createTargetOutOfPocketNodeFromPayload(payload: any): boolean {
    const cashApplyMethod = selectCashApplyMethodFromPayload(payload);
    return cashApplyMethod === CashOptions.AddCashOffsetToPrice || false;
}

function getInitOfferSource(payload: any): DealerSource {
    const offer = getOfferFromInitPayload(payload);
    return offer.source || {};
}

function getInCompleteDealDetails(payload: any): IncompleteDealDetails {
    const offer = getOfferFromInitPayload(payload);
    return offer.incompleteDealDetails || {};
}

const selectPaymentFromPayload = (payload: any) => {
    const offer = getOfferFromInitPayload(payload);
    return offer.payment || {};
};

const selectInvalidDealDetailsFromPayload = (payload: any): PartialInitOfferPayload => {
    const InvalidDealOfferPayload: PartialInitOfferPayload = {
        dealExchangeDealId: selectDealExchangeDealIdFromPayload(payload),
        dealExchangeVersionId: selectDealExchangeVersionIdFromPayload(payload),
        coBuyer: selectCoBuyerFromPayload(payload),
        incompleteDealDetails: getInCompleteDealDetails(payload),
        sessionId: selectSessionIdFromPayload(payload),
        featureToggles: selectFeatureTogglesFromPayload(payload) as ExtendedFeatureToggles,
        shopper: selectShopperFromPayload(payload),
        vehicle: selectVehicleFromPayload(payload)
    };
    return InvalidDealOfferPayload;
};

/**
 * Map an incoming init action into the payload used to initialize offer nodes
 * (including payment data) or return the original action if not handled.
 * @param action - The Flux Standard Action containing the init payload
 * @returns The transformed INIT_OFFER/INIT_INVALID_OFFER action or the original action
 */
export function mapAction(action: AnyFSA) {
    const { type, payload } = action;
    const validInitActions: Record<string, boolean> = {
        [INIT_PENCIL_SUCCESS]: true,
        [INIT_PENCIL_INVALID_DEAL]: true
    };
    if (validInitActions[type]) {
        const partialInitOfferPayload = selectInvalidDealDetailsFromPayload(payload);
        switch (type) {
            case INIT_PENCIL_SUCCESS: {
                const initOfferPayload: InitOfferPayload = {
                    ...partialInitOfferPayload,
                    [OFFER_ENCRYPTION_FIELDS.PAYMENT]: selectExtraDataFromPayload(payload),
                    cashApplyMethod: selectCashApplyMethodFromPayload(payload) as string,
                    creditScore: selectCreditScoreFromPayload(payload),
                    creditScoreSource: selectCreditScoreSourceFromPayload(payload),
                    creditTiers: selectCreditTiersFromPayload(payload),
                    incentives: selectIncentivesFromPayload(payload),
                    isTargetOutOfPocket: createTargetOutOfPocketNodeFromPayload(payload),
                    leaseAnnualMiles: selectLeaseAnnualMilesFromPayload(payload),
                    message: selectMessageFromPayload(payload),
                    monthlyPayment: selectMonthlyPaymentFromPayload(payload),
                    monthlyPaymentWithoutProducts: selectMonthlyPaymentWithoutProductsFromPayload(payload),
                    payment: selectPaymentFromPayload(payload),
                    penciledInputs: selectPenciledInputsFromPayload(payload),
                    settings: selectSettingsFromPayload(payload),
                    source: getInitOfferSource(payload),
                    terms: selectTermsFromPayload(payload),
                    toggles: selectTogglesFromPayload(payload) as Partial<FeatureToggles>,
                    totalFinanced: selectTotalFinancedFromPayload(payload)
                };

                if (Config.getInstance().getConfig('isTermIdWithDownPayment')) {
                    syncDefaultTermIds(initOfferPayload);
                }

                return {
                    type: 'INIT_OFFER',
                    payload: initOfferPayload
                };
            }
            case INIT_PENCIL_INVALID_DEAL: {
                return {
                    type: 'INIT_INVALID_OFFER',
                    payload: partialInitOfferPayload
                };
            }
        }
    }
    return action;
}
